'use client';

import { InlineExecutionToolCard } from '../shared/inline-execution-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { formatCountedToolCallLabel } from '../shared/format-counted-tool-call-label';
import type { CardRenderContext } from '../../tool-config-types';
import { normalizeToolName } from '@terra/shared-chat-trace/trace-view/utils/tool-config.helpers';
import {
  getInternalNetworkToolResultCount,
  renderInternalNetworkToolOutputBody,
} from './ad-tool-output-body';
import { INTERNAL_NETWORK_TOOL_NAMES } from './internal-network-tool-names';

function getDisplayText({
  ctx,
  count,
}: {
  ctx: CardRenderContext;
  count?: number;
}): string {
  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';

  if (isRunning) {
    return ctx.labelPresent;
  }

  if (count !== undefined && count > 0) {
    return formatCountedToolCallLabel({
      label: ctx.labelPast,
      count,
    });
  }

  return ctx.labelPast;
}

export function renderInternalNetworkAdToolCard(ctx: CardRenderContext) {
  const count = getInternalNetworkToolResultCount({
    toolName: ctx.toolName,
    output: ctx.toolCallData.output,
  });
  const body = renderInternalNetworkToolOutputBody({
    toolName: ctx.toolName,
    output: ctx.toolCallData.output,
  });

  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';

  if (
    !isRunning &&
    !body &&
    !ctx.toolCallData.error &&
    ctx.toolCallData.status === 'completed'
  ) {
    return null;
  }

  return (
    <InlineExecutionToolCard
      title={getDisplayText({ ctx, count })}
      status={ctx.toolCallData.status}
      error={ctx.toolCallData.error}
      headerActions={getHITLArtifactHeaderActions(ctx)}
      defaultExpanded={false}
      autoCollapseWhenComplete={ctx.autoCollapseWhenComplete}
      forceExpanded={ctx.forceExpanded}
    >
      {body}
    </InlineExecutionToolCard>
  );
}

export function renderListAdEntitiesCard(ctx: CardRenderContext) {
  return renderInternalNetworkAdToolCard(ctx);
}

export function renderQueryAdGraphCard(ctx: CardRenderContext) {
  return renderInternalNetworkAdToolCard(ctx);
}

export function renderGetAdEntityDetailsCard(ctx: CardRenderContext) {
  return renderInternalNetworkAdToolCard(ctx);
}

export function renderGetAdEntityRelationshipsCard(ctx: CardRenderContext) {
  return renderInternalNetworkAdToolCard(ctx);
}

export function renderDiscoverySignalCard(ctx: CardRenderContext) {
  return renderInternalNetworkAdToolCard(ctx);
}

export function isInternalNetworkDiscoveryToolName(toolName: string): boolean {
  const normalized = normalizeToolName(toolName);

  return (
    normalized ===
      normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.CREATE_DISCOVERY_SIGNAL) ||
    normalized ===
      normalizeToolName(
        INTERNAL_NETWORK_TOOL_NAMES.GET_EXISTING_DISCOVERY_SIGNALS,
      ) ||
    normalized ===
      normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.CREATE_SCENARIO_DRAFT)
  );
}
